/**
 * Generates and downloads a real, double-clickable desktop shortcut to this site.
 *
 * Why this exists: on every non-Chromium browser there is no way to install the PWA from script
 * (Safari and Firefox expose no install API at all), and there is no way to add a bookmark from
 * script either — `window.sidebar.addPanel` and `window.external.AddFavorite` were both removed
 * years ago and are absent from all current engines. Telling the user to press ⌘D is the usual
 * fallback, but it is an instruction, not an action.
 *
 * Writing a shortcut file is the one thing a page genuinely *can* do here: every engine supports
 * the `download` attribute plus `URL.createObjectURL`, so we can hand the user an actual file
 * they can keep on the Desktop, Dock, or taskbar. It is a browser shortcut rather than an
 * installed app — it opens in a normal tab, with none of the standalone-window behaviour a real
 * PWA install gives you — so callers should say so rather than calling it an install.
 */

export type ShortcutPlatform = 'macos' | 'windows' | 'linux'

/** Stable product name for the file. Deliberately NOT `document.title`, which carries the current
 *  view ("Map — …", "List view — …") and would bake a transient UI state into the filename. */
export const SHORTCUT_TITLE = 'Inglis Enabled Home Showcase'

export function detectShortcutPlatform(userAgent: string = navigator.userAgent): ShortcutPlatform {
  if (/Macintosh|Mac OS X/.test(userAgent)) return 'macos'
  if (/Windows/.test(userAgent)) return 'windows'
  return 'linux'
}

/**
 * Whether a downloaded shortcut file will still be usable once it lands.
 *
 * Firefox appends `.download` to any file it saves with a `.url` or `.desktop` extension — a
 * deliberate anti-execution measure, and it happens regardless of the blob's MIME type (verified
 * across `application/internet-shortcut`, `text/plain`, `application/octet-stream` and
 * `text/uri-list`). A `Shortcut.url.download` file is inert on Windows, and there is no alternate
 * extension either platform accepts, so the honest answer on Firefox/Windows and Firefox/Linux is
 * that we cannot deliver a working shortcut at all. `.webloc` is left alone, so Firefox on macOS
 * is fine, as is every other browser.
 */
export function canDeliverShortcutFile(userAgent: string = navigator.userAgent): boolean {
  const isFirefox = /Firefox/.test(userAgent)
  return !isFirefox || detectShortcutPlatform(userAgent) === 'macos'
}

/** Strips characters that are illegal in filenames on any of the three target platforms. */
function safeFileName(title: string): string {
  return title.replace(/[\\/:*?"<>|]/g, '').trim() || 'shortcut'
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export interface ShortcutFile {
  name: string
  mimeType: string
  contents: string
}

export function buildShortcutFile(url: string, title: string, platform: ShortcutPlatform): ShortcutFile {
  const base = safeFileName(title)
  switch (platform) {
    case 'macos':
      // .webloc is a plist; Finder resolves it to the URL on double-click.
      return {
        name: `${base}.webloc`,
        mimeType: 'application/xml',
        contents:
          '<?xml version="1.0" encoding="UTF-8"?>\n' +
          '<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">\n' +
          '<plist version="1.0">\n' +
          '<dict>\n' +
          '\t<key>URL</key>\n' +
          `\t<string>${escapeXml(url)}</string>\n` +
          '</dict>\n' +
          '</plist>\n',
      }
    case 'windows':
      // .url is an INI-style file Explorer treats as an Internet Shortcut. CRLF is what Windows
      // writes itself; LF-only files still work but look malformed in Notepad.
      return {
        name: `${base}.url`,
        mimeType: 'application/internet-shortcut',
        contents: ['[InternetShortcut]', `URL=${url}`, 'IconIndex=0', ''].join('\r\n'),
      }
    case 'linux':
      // freedesktop.org .desktop entry. Must be marked executable by the user before most file
      // managers will launch it — noted in the UI copy rather than silently failing.
      return {
        name: `${base}.desktop`,
        mimeType: 'application/x-desktop',
        contents: [
          '[Desktop Entry]',
          'Type=Link',
          `Name=${title}`,
          `URL=${url}`,
          'Icon=text-html',
          '',
        ].join('\n'),
      }
  }
}

/** Writes the shortcut file to the user's downloads. Returns the filename that was produced. */
export function downloadDesktopShortcut(
  // origin + pathname, not `location.href`: the app keeps its view state in memory rather than the
  // URL, but any stray query/hash would otherwise be frozen into the saved shortcut.
  url: string = `${window.location.origin}${window.location.pathname}`,
  title: string = SHORTCUT_TITLE,
  platform: ShortcutPlatform = detectShortcutPlatform()
): string {
  const file = buildShortcutFile(url, title, platform)
  const blob = new Blob([file.contents], { type: file.mimeType })
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = file.name
  // Firefox only honours a programmatic click on an anchor that is in the document.
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Revoking synchronously can cancel the download in some builds; defer past the click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  return file.name
}
